// asyncStore.ts
import { AxiosResponse } from 'axios';
import { atom } from 'jotai';
import { getDefaultStore } from 'jotai';
import { onPromise, PromiseOptions } from 'common/axios/onPromise';

export const AsyncStatus = {
	default: 'default',
	pending: 'pending',
	ready: 'ready',
	error: 'error',
} as const;

type AsyncStatus = typeof AsyncStatus[keyof typeof AsyncStatus];

interface AsyncState {
	status: AsyncStatus;
	data: any;
	error: any;
	headers: any;
}

// 초기 상태 정의
const initialState: AsyncState = {
	status: AsyncStatus.default,
	data: null,
	error: null,
	headers: null,
};

// 메인 atom 생성 함수
export function createAsyncStore() {
	const stateAtom = atom<AsyncState>({ ...initialState });

	const actions = {
		onDefault: () => (getDefaultStore().set(stateAtom, { ...initialState })),
		onPending: () => {
			getDefaultStore().set(stateAtom, (prev) => ({
				...prev,
				status: AsyncStatus.pending,
				error: null,
			}))
		},
		onReady: (data: any, headers?: any) => {
			getDefaultStore().set(stateAtom, {
				status: AsyncStatus.ready,
				data,
				headers: headers || null,
				error: null,
			})
		},
		onMore: (data: any, datakey: string, headers?: any) =>
			getDefaultStore().set(stateAtom, (prev) => ({
				status: AsyncStatus.ready,
				data: {
					...prev.data,
					[datakey]: [...(prev.data?.[datakey] || []), ...data[datakey]],
				},
				headers: headers || null,
				error: null,
			})),

		onError: (error: any, headers?: any) =>
			getDefaultStore().set(stateAtom, {
				status: AsyncStatus.error,
				data: null,
				headers: headers || null,
				error: error,
			}),

		onGetAll: (promise: () => Promise<AxiosResponse>, options?: PromiseOptions) => {
			onPromise(actions)(promise, options);
		},



	};

	const derived = {
		isPendingAtom: atom((get) => get(stateAtom).status === AsyncStatus.pending),
		isReadyAtom: atom((get) => get(stateAtom).status === AsyncStatus.ready),
		isErrorAtom: atom((get) => get(stateAtom).status === AsyncStatus.error),
		isDefaultAtom: atom((get) => get(stateAtom).status === AsyncStatus.default),
	};

	return {
		stateAtom,
		...derived,
		...actions,
	};
}
